import React, { useState, useEffect } from "react";
import { TailwindWrapper } from "@utils/TailwindWrapper";
import { Button, Text, Icon, Accordion, Tooltip, Dialog, Link, TwTabs, Input, SliderBar } from "@ui-component-library";
import ReviewSlider from "../ReviewSlider";
import { cn } from "@utils/cn";
import { getSubscriptionPrices } from "../../../../../api/services/subscription";
import SelectPropertyDialog from "@vb-django/components/minskog/comps/SubscriptionCheckout/components/SelectPropertyDialog";
import { useSubscriptionOptional } from "@vb-django/components/minskog/context/SubscriptionCheckoutContext";
import { useLogin } from "../../../../login/context/LoginContext";
import { forceLoginToPurchasePlus } from "@utils/abTestCookies.js";

const DEMO_LINK = "https://2e1iip.share-eu1.hsforms.com/2XeAiDeueTBmhUZMmOkm-Mw";
const staticPath = typeof window !== "undefined" ? (window as unknown as { static_path: string }).static_path ?? "" : "";

// —— Hero copy ——
const HERO = {
  title: "Skydda dina värden genom att agera i tid",
  paragraph: "Med Skogsövervakning från Treebula får du notiser om viktiga händelser i din skog och hur du kan agera i tid innan värden går förlorade. Din skog alltid under uppsikt.",
  buttonBook: "Boka genomgång",
  buttonPlus: "Bli pluskund",
  heroImageAlt: "Skogsövervakning exempel",
};

// —— Bento copy ——
const BENTO = {
  title: "Din skog alltid under uppsikt med Treebulas Skogsövervakning",
  paragraph: "Skogsövervakningen fungerar som ett digitalt hemlarm för skogen. Med hjälp av satellitbilder kan akuta händelser som vindfällen och angrepp från barkborre eller döda träd snabbt identifieras och lokaliseras. Ger en tydlig helhetsbild av skogens sårbarhet genom att identifiera utsatta kantzoner.",
  cards: [
    { img: "vindanalys.png", imgAlt: "Vindanalys exempel", title: "Vindanalys", paragraph: "Genom att löpande analysera vindförhållandena på din fastighet kan vi visualisera var risken för vindfällen är störst." },
    { img: "vader.png", imgAlt: "Väder exempel", title: "Väder (Temperatur & Nederbörd)", paragraph: "Få uppdaterad och relevant väderdata direkt kopplad till din skogsfastighet." },
    { img: "granbarkborre.png", imgAlt: "Granbarkborre exempel", title: "Granbarkborre (Skadeövervakning)", paragraph: "Vår smarta övervakning skannar av din skog med satellitbilder för att snabbt identifiera och varna dig för vindfällen och angrepp från barkborre eller döda träd." },
    { img: "kraftig.png", imgAlt: "Kraftig vind exempel", title: "Kraftig vind (Riskbedömning)", paragraph: "Vi omvandlar komplex data till konkreta insikter. Genom att analysera historiska och aktuella vinddata beräknar vi risken för skada på din fastighet." },
    { img: "brand.png", imgAlt: "Brand och risk exempel", title: "Brand och risk (Samlad bedömning)", paragraph: "Få en samlad riskbedömning av de största hoten mot din skog. Vi kombinerar data för brand, barkborre, väder och torka för att ge dig en överskådlig status." },
  ] as const,
};

// —— CTA copy ——
const CTA = {
  title: "Ta kontroll över din skog och skydda din investering idag",
  paragraph: "Förlora inte värden på grund av vindfällen eller barkborreangrepp. Med Skogsövervakning får du ett vakande öga på din fastighet. Kom igång och ta kontroll över värdet för din skog.",
  buttonPrimary: "Bli pluskund",
  buttonSecondary: "Boka genomgång",
};

// —— Pricing copy ——
const BAS_FEATURES = {
  subtitle: "Detta ingår",
  info: [
    { text: "Sälja virke & biobränsle", details: "" },
    { text: "Begränsad digital skogsbruksplan", details: "" },
    { text: "Enkel kolkrediter översikt", details: "" },
  ],
};

const PLUS_FEATURES = {
  subtitle: "Allt i Bas, inklusive:",
  info: [
    { text: "Digital skogsbruksplan", details: "" },
    { text: "Skogsdata och analys", details: "" },
    { text: "Virkespriser och värdering", details: "" },
    { text: "Sälja virke och biobränsle", details: "" },
    { text: "Skogsövervakning och risker", details: "" },
    { text: "Marknadsinformation", details: "" },
    { text: "Kolkrediter est. värde", details: "" },
    { text: "Personlig rådgivning", details: "" },
  ],
};

const PRICING_SECTION = {
  subHeader: "Priser och paket",
  titleLine1: "Ta kontroll över ",
  titleLine2: "värdet i din skog",
  paragraph: "All data, analys och marknadsinsikt du behöver för att fatta trygga beslut och skapa långsiktigt värde. Utan bindningstider.",
  
};

// —— FAQ copy ——
const FAQ = {
  title: "Vanliga frågor (FAQ)",
  paragraph: "",
  items: [
    { question: "Hur ofta uppdateras informationen?", answer: "Fallna träd och toppbrott: var 2-3 vecka (sommartid). Granbarkborre: analyseras i september och oktober varje år. Väderdata: daglig uppdatering." },
    { question: "Hur snabbt får jag veta om något händer?", answer: "Vi skickar en notifiering så fort akuta förändringar upptäcks efter skanning." },
    { question: "Kan jag köpa tjänsten för flera fastigheter?", answer: "Ja, du kan lägga till flera fastigheter under samma konto. Varje fastighet köps separat." },
    { question: "Om jag får en varning men inte har möjlighet att åka ut i skogen, vad kan jag göra då?", answer: "Vi erbjuder drönarflygning med högupplösta bilder och möjlighet till djupgående analys av misstänkta angrepp, toppbrott eller fallna träd. Beställ via plattformen i inloggat läge - bild och analys levereras inom ca två veckor." },
    { question: "Behöver jag installera något?", answer: "Nej. Allt sker via vår digitala plattform, du får tillgång direkt efter köp." },
  ],
};

// —— Sections (only optional callbacks for behaviour) ——

const HeroSection = ({
  onBookDemo,
  onBliPluskund,
}: {
  onBookDemo?: () => void;
  onBliPluskund?: () => void;
}) => (
  <div className="bg-cloud-50">
    <div className="container">
      <TailwindWrapper>
        <div className="grid grid-cols-1 lg:grid-cols-2 px-6 gap-16 py-24">
          <div className="grid grid-row content-center order-2 lg:order-1">
            <div className="grid grid-row gap-6">
              <Text variant="text-4xl/leading-none/medium">{HERO.title}</Text>
              <Text variant="text-lg/leading-normal/normal" className="text-[#737373]">{HERO.paragraph}</Text>
            </div>
            <div className="pt-8 flex justify-start gap-3">
              <Button tw onClick={onBookDemo} variant="outline">{HERO.buttonBook}</Button>
              <Button tw onClick={onBliPluskund} variant="primary">
                {HERO.buttonPlus}
                <Icon color="white-50" name="ArrowRightSimple" />
              </Button>
            </div>
          </div>
          <div className="order-1 lg:order-2 flex justify-center">
            <img src={`${staticPath}img/dfr/overvarkning-hero.png`} alt={HERO.heroImageAlt} className="w-full h-auto object-contain" />
          </div>
        </div>
      </TailwindWrapper>
    </div>
  </div>
);

const BentoCard = ({ img, title, paragraph }: { img: React.ReactNode; title: string; paragraph: string }) => (
  <div className="flex flex-col h-full rounded-xl overflow-hidden">
    <div className="h-[336px] w-full overflow-hidden">{img}</div>
    <div className="p-6 flex flex-col gap-2 bg-cloud-200 sm:h-[150px] md:h-[300px] lg:h-[150px]">
      <Text variant="text-lg/leading-normal/medium">{title}</Text>
      <Text variant="text-sm/leading-normal/normal" className="text-[#737373]">{paragraph}</Text>
    </div>
  </div>
);

const BentoSection = () => (
  <div className="bg-cloud-50">
    <div className="container">
      <TailwindWrapper>
        <div className="py-24 px-6">
          <div className="flex justify-center">
            <div className="w-[576px] flex flex-col gap-5">
              <Text variant="text-3xl/leading-normal/medium" className="text-center">{BENTO.title}</Text>
              <Text variant="text-sm/leading-normal/normal" className="text-[#737373] text-center">{BENTO.paragraph}</Text>
            </div>
          </div>
          <div className="grid grid-cols-12 pt-12 gap-3 items-stretch">
            {BENTO.cards.slice(0, 3).map((card) => (
              <div key={card.title} className="col-span-12 md:col-span-4 h-[460px]">
                <BentoCard
                  img={<img src={`${staticPath}img/dfr/${card.img}`} alt={card.imgAlt} className="w-full h-full object-cover" />}
                  title={card.title}
                  paragraph={card.paragraph}
                />
              </div>
            ))}
            <div className="col-span-12 md:col-span-8 h-[460px]">
              <BentoCard
                img={<img src={`${staticPath}img/dfr/${BENTO.cards[3].img}`} alt={BENTO.cards[3].imgAlt} className="w-full h-full object-contain bg-[#F2F0ED]" />}
                title={BENTO.cards[3].title}
                paragraph={BENTO.cards[3].paragraph}
              />
            </div>
            <div className="col-span-12 md:col-span-4 h-[460px]">
              <BentoCard
                img={<img src={`${staticPath}img/dfr/${BENTO.cards[4].img}`} alt={BENTO.cards[4].imgAlt} className="w-full h-full object-cover" />}
                title={BENTO.cards[4].title}
                paragraph={BENTO.cards[4].paragraph}
              />
            </div>
          </div>
        </div>
      </TailwindWrapper>
    </div>
  </div>
);

const CustomerTestimonialSection = () => (
  <div className="bg-[#E5E1DC]">
    <div className="container">
      <TailwindWrapper>
        <div className="py-24 px-6 md:px-20">
          <ReviewSlider />
        </div>
      </TailwindWrapper>
    </div>
  </div>
);

const CTASection = ({
  onCtaClick,
  onSecondaryClick,
}: {
  onCtaClick?: () => void;
  onSecondaryClick?: () => void;
}) => (
  <div className="bg-[#0F0F0F]">
    <div className="container">
      <TailwindWrapper>
        <div className="py-24 px-6 flex justify-center">
          <div className="flex flex-col text-white text-center gap-5 max-w-[576px]">
            <Text variant="text-3xl/leading-normal/medium">{CTA.title}</Text>
            <Text variant="text-base/leading-normal/normal">{CTA.paragraph}</Text>
            <div className="flex justify-center mt-5 gap-4">
              <Button tw variant="outline" className="text-white-50" onClick={onSecondaryClick}>{CTA.buttonSecondary}</Button>
              <Button tw variant="outline" className="bg-white hover:text-white" onClick={onCtaClick}>
                {CTA.buttonPrimary}
                <Icon name="ArrowRightSimple" />
              </Button>
            </div>
          </div>
        </div>
      </TailwindWrapper>
    </div>
  </div>
);

const PricingCard = ({
  title,
  priceCardButton,
  features,
  backgroundColor = "white",
}: {
  title: string;
  priceCardButton?: { state: boolean; onClick?: () => void; text: string };
  features: { subtitle: string; info: { text: string; details: string }[] };
  backgroundColor?: "white" | "black";
}) => {
  const [openDialog, setOpenDialog] = useState({ state: false, text: "", details: "" });
  const [isTablet, setIsTablet] = useState(typeof window !== "undefined" && window.innerWidth < 1024);
  const subscription = useSubscriptionOptional();
  const { isLogged } = useLogin();

  useEffect(() => {
    const handleResize = () => setIsTablet(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isPlus = backgroundColor === "black";

  const isBas = !isPlus && !priceCardButton?.state;


  
  return (
    <TailwindWrapper>
      <div className={`p-5 md:p-8 rounded-xl shadow-xl min-w-[296px] md:w-[396px] min-h-[656px] ${isPlus ? "bg-[#22252A] border-white" : "bg-white"}`}>
        <div className="flex justify-between items-center mb-4">
        <Text variant="text-lg/leading-normal/medium" className={!isBas && "text-[#FBFBFB]"}>{title}</Text>
        {isBas && <Text variant="text-xs/leading-normal/medium" className="bg-[#F5F5F5] px-2 py-1 rounded-xl">Kostnadsfritt</Text>}
        {!isBas && <Text variant="text-xs/leading-normal/medium" className="bg-[#DFFF5D]/20 px-2 py-1 rounded-xl text-[#ECFF8C]">Rekommenderas</Text> }
        </div>
        <div className="min-h-[200px] flex flex-col">
            {isBas && (
              <>
                <div className="flex items-baseline">
                  <Text variant="text-4xl/leading-normal/medium" className="mr-1">0</Text>
                  <Text variant="text-base/leading-normal/normal">kr/år</Text>
                </div>
                <Text variant="text-xs/leading-normal/normal" className="mt-2 text-[#737373]">Sälj virke och hantera din skog med en grundläggande överblick. Ett tryggt första steg.</Text>
                <Button tw variant="secondary" className="w-full mt-auto mb-8" onClick={() => { window.open("/min-skog/", "_self"); }}>Kom igång</Button>
              </>
            )}
            {!isBas && (
              <>
                <div className="flex items-baseline text-[#FBFBFB]">
                  <Text variant="text-xl/leading-normal/normal" className="mr-2">Från</Text>
                  <Text variant="text-4xl/leading-normal/medium" className="mr-1">1 399</Text>
                  <Text variant="text-base/leading-normal/normal">kr/år</Text>
                </div>
                <Text variant="text-xs/leading-normal/normal" className="mt-2 text-[#FBFBFB]">*Priset baseras på fastighetens produktiva skogsareal.</Text>
                <Button
                  tw
                  variant="secondary"
                  className="w-full mt-auto mb-8 bg-[#ECFF8C]"
                  onClick={() => {
                    if (priceCardButton?.onClick) {
                      priceCardButton.onClick();
                      return;
                    }
                    if (
                      subscription?.openCheckout &&
                      !forceLoginToPurchasePlus(isLogged)
                    ) {
                      subscription.openCheckout();
                    } else {
                      window.open("/min-skog/?subscription-checkout=true", "_self");
                    }
                  }}
                >
                  {priceCardButton?.state && priceCardButton?.text ? priceCardButton.text : "Uppgradera till Plus"}
                </Button>
              </>
            )}
        </div>
        <Text variant="text-sm/leading-normal/medium" className={`${isPlus ? "text-white" : ""} text-left`}>{features.subtitle}</Text>
        {features.info.map((feature, index) =>
          !isTablet ? (
            <Tooltip key={index} aria-label={feature.text} content={[feature.text, feature.details].filter(Boolean).join("\n\n")}>
              <div className="flex justify-between items-center mt-4">
                <div className="flex justify-start items-center">
                  <Icon tw name="CheckSimple" className={cn(isPlus && "fill-white", "mr-3 shrink-0")} />
                  <Text variant="text-sm/leading-normal/normal" className={`${isPlus ? "text-white" : ""} text-left`}>{feature.text}</Text>
                </div>
              </div>
            </Tooltip>
          ) : (
            <div
              key={index}
              className="flex justify-between items-center mt-4 cursor-pointer"
              onClick={() => setOpenDialog({ state: true, text: feature.text, details: feature.details })}
              onKeyDown={(e) => e.key === "Enter" && setOpenDialog({ state: true, text: feature.text, details: feature.details })}
              role="button"
              tabIndex={0}
            >
              <div className="flex justify-start items-center">
                <Icon tw name="CheckSimple" className={cn(isPlus && "fill-white", "mr-3 shrink-0")} />
                <Text variant="text-sm/leading-normal/normal" className={`${isPlus ? "text-white" : ""} text-left`}>{feature.text}</Text>
              </div>
            </div>
          )
        )}
      </div>
      <Dialog isOpen={openDialog.state} size="small">
        <Dialog.Header className="justify-between items-center">
          <Text variant="text-sm/leading-normal/normal">{openDialog.text}</Text>
          <Button tw variant="outline" size="icon" onClick={() => setOpenDialog({ state: false, text: "", details: "" })}>
            <Icon name="CrossSimple" className="color-charcoal-500" size="medium" />
          </Button>
        </Dialog.Header>
        <Dialog.Content>
          <Text variant="text-sm/leading-normal/normal">{openDialog.details}</Text>
        </Dialog.Content>
      </Dialog>
    </TailwindWrapper>
  );
};

const PricingSection = ({
        onOpenCheckout,
        children,
      }: {
        onOpenCheckout?: () => void;
        title?: string;
        paragraph?: React.ReactNode;
        children?: React.ReactNode;
      }) => (
      <TailwindWrapper>
        <div className="py-24 px-4 bg-[#F6F5F4]">
          <div className="flex justify-center mb-12">
            <div className="w-[576px] max-w-[576px] flex flex-col items-center">
              <div className="flex flex-col items-center5">
                <Text variant="text-base/leading-normal/normal" className="text-[#737373] text-center mb-4">{PRICING_SECTION.subHeader}</Text>
                <Text variant="text-3xl/leading-normal/medium" className="text-center w-full">{PRICING_SECTION.titleLine1}</Text>
                <Text variant="text-3xl/leading-normal/medium" className="text-center w-full mb-4">{PRICING_SECTION.titleLine2}</Text>
                  <Text variant="text-base/leading-normal/normal" className="text-[#737373] text-center">All data, analys och marknadsinsikt du behöver för att fatta <br />
                  trygga beslut och skapa långsiktigt värde. < br />Utan bindningstider.</Text>
              </div>
            </div>
          </div>
          <div className="w-full flex items-center justify-center">
          {children !== null ? children : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PricingCard title="Bas" features={BAS_FEATURES} />
              <PricingCard
                title="Treebula Plus"
                priceCardButton={onOpenCheckout ? { state: true, onClick: onOpenCheckout, text: "Se pris och beställ" } : undefined}
                features={PLUS_FEATURES}
                backgroundColor="black"
              />
            </div>
          )}
          </div>
        </div>
      </TailwindWrapper>
);

const SliderSection = ({ onScrollToComparison }: { onScrollToComparison?: () => void }) => {
  const subscription = useSubscriptionOptional();
  const { isLogged } = useLogin();
  const [tabValue, setTabValue] = useState("hectare");
  const [hectare, setHectare] = useState([50]);
  const [subscriptionPrices, setSubscriptionPrices] = useState([]);
  const [subscriptionPriceTicks, setSubscriptionPriceTicks] = useState([]);
  const [searchPropertySize, setSearchPropertySize] = useState()
  const [showPropertyDialog, setShowPropertyDialog] = useState(false);

  const handleChangeHectare = (val) => {
    const closestTick = subscriptionPriceTicks.reduce((prev, curr) => Math.abs(curr - val[0]) < Math.abs(prev - val[0]) ? curr : prev, -9999);
    setHectare([closestTick,])
  }

  const fetchSubscriptionPrices = async () => {
    const { ok, status, data } = await getSubscriptionPrices(true);
    if (ok) {
      setSubscriptionPrices(data.products.plus.prices);
      setSubscriptionPriceTicks(data.products.plus.prices.map(p => p.min_ha))
    } else {
      console.error("Price fetch error: ", status, data);
    } 
  };

  useEffect(() => {
    fetchSubscriptionPrices()
  }, [])

  
  const price = Math.round(subscriptionPrices.find((sp) => hectare[0] >= sp.min_ha && (hectare[0] <= sp.max_ha || sp.max_ha===null))?.price).toLocaleString("sv-SE")
  
  return (
    <TailwindWrapper>
      <div className="py-24 px-4 bg-[#F6F5F4]">
        <div className="flex justify-center">
          <div className="w-[576px] max-w-[576px] flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-5 w-full">
              <Text variant="text-3xl/leading-normal/medium" className="text-center w-full">
                Ditt pris, baserat på din skogsfastighet
              </Text>
              <div className="flex w-4/5 flex-col items-center gap-8">
                <Text variant="text-base/leading-normal/normal" className="text-[#737373] text-center">
                  Sök efter din fastighet eller fyll i storlek för att se ett estimerat pris. 
                </Text>
                <TwTabs
                  variant="pill"
                  value={tabValue}
                  tabs={[
                    { label: "Antal hektar", value: "hectare" },
                    { label: "Din fastighet", value: "property" },
                  ]}
                  onChange={(value) => {
                    setTabValue(value);
                    if (value === 'property') setShowPropertyDialog(true);
                  }}
                />
                <Input
                  tw
                  className="w-full"
                  name="size"
                  type="number"
                  variant="fluid"
                  label={'Produktiv skogsmark (ha)'}
                  value={String(hectare[0]) || searchPropertySize}
                  onChange={(val: string) => {
                    const num = Number(val);
                    if (!isNaN(num)) setHectare([Math.min(5001, Math.max(0, num))]);
                  }}
                />
                <SliderBar
                  className="w-full"
                  value={hectare}
                  min={0}
                  max={5001}
                  step={10}
                  onValueChange={handleChangeHectare}
                />
                <div className="flex items-baseline bg-[#ECFF8C] rounded-2xl w-full p-8 justify-center flex-wrap">
                  <Text variant="text-4xl/leading-normal/medium" className="mr-1">
                    {subscriptionPrices.length > 0 ? price : "-"}
                  </Text>
                  <Text variant="text-base/leading-normal/normal" className="whitespace-nowrap">kr/år</Text>
                </div>
                <Button
                  tw
                  variant="primary"
                  className="w-full"
                  onClick={() => {
                    if (
                      subscription?.openCheckout &&
                      !forceLoginToPurchasePlus(isLogged)
                    ) {
                      subscription.openCheckout();
                    } else {
                      window.open("/min-skog/?subscription-checkout=true", "_self");
                    }
                  }}
                >
                  Uppgradera till Plus
                </Button>
                <Button tw variant="tertiary" className="flex items-center gap-2 w-full -mt-4" 
                  onClick={(e) => {
                  e.preventDefault();
                  onScrollToComparison?.();
                }}>
                  <Text variant="text-sm/leading-normal/medium">Se allt som ingår</Text>
                  <Icon name="ArrowRightSimple" size="small" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {tabValue === 'property' &&
        <SelectPropertyDialog
          isOpen={showPropertyDialog}
          onClose={() => setShowPropertyDialog(false)}
          userProperties={[]}
          selectedProperties={[]}
          onSelect={() => {}}
          onConfirm={(result) => {
            setSearchPropertySize(result);
            const areaHa = result?.properties?.[0]?.area_ha;
            if (areaHa != null) {
              setHectare([areaHa]);
            }
            setShowPropertyDialog(false);
          }}
          startedAsGuest
        />
      }
    </TailwindWrapper>
  );
};

const FAQComponent = ({ question, answer }: { question: string; answer: React.ReactNode }) => (
  <Accordion className="border-b border-[#E6E6E6] py-4">
    <Accordion.Header>
      <Text variant="text-sm/leading-normal/medium">{question}</Text>
    </Accordion.Header>
    <Accordion.Content>{answer}</Accordion.Content>
  </Accordion>
);

const FAQSection = ({ containerRef }: { containerRef?: React.RefObject<HTMLDivElement> }) => {
  return (
  <TailwindWrapper>
    <div ref={containerRef} className="bg-cloud-50">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 py-24 px-6">
          <div>
            {FAQ.items.map((item) => (
              <FAQComponent
                key={item.question}
                question={item.question}
                answer={<Text variant="text-sm/leading-normal/medium">{item.answer}</Text>}
              />
            ))}
            <FAQComponent
              question="Var kan jag vända mig om jag har fler frågor?"
              answer={
                <Text variant="text-sm/leading-normal/medium">
                  Du är välkommen att kontakta oss på{" "}
                  <Link href="mailto:info@treebula.se" tw target="blank" underline>info@treebula.se</Link> så återkommer vi så snabbt vi kan.
                </Text>
              }
            />
            <FAQComponent
              question="Hur kan jag få hjälp med att hitta någon som kan hjälpa till med min skog?"
              answer={
                <Text variant="text-sm/leading-normal/medium">
                  Treebula har ett brett nätverk. Det gör du enklast via att maila till{" "}
                  <Link href="mailto:info@treebula.se" tw target="blank" underline>info@treebula.se</Link>
                </Text>
              }
            />
          </div>
        </div>
      </div>
      </div>
      </TailwindWrapper>
    );
};

export {
  HeroSection,
  BentoCard,
  BentoSection,
  CustomerTestimonialSection,
  CTASection,
  PricingCard,
  PricingSection,
  SliderSection,
  FAQComponent,
  FAQSection,
};
export { DEMO_LINK };
